/**
 * NetworkQualityPill
 *
 * Discreet on-screen indicator that surfaces sustained audio-pipeline pressure to the
 * user. The audio playback path adapts to slow / jittery networks by growing its buffer
 * (up to ~2.9s on desktop), which the user hears as added latency or occasional clicks
 * even though the app is doing the right thing. Without an indicator the user assumes
 * "the app is broken"; with one they understand the audio is fighting the network.
 *
 * Reads `networkQuality` from the playback diagnostics — a per-response rolling
 * aggregate (last 60s). Earlier versions polled the *transient* `prebufferTimeMs` /
 * `jitterDetected`, which drop to baseline between conversational turns and so rarely
 * stayed "degraded" for the 3-second hysteresis window even when the network was
 * genuinely bad. The cumulative signal does not have that problem.
 *
 * Visibility logic — it is an auto-dismissing notice, NOT a state mirror:
 *   - Hidden entirely when the session is paused (intentional pause ≠ slow network).
 *   - Shows for a fixed SHOW_DURATION_MS (~4s) on the rising edge of degradation, then
 *     hides itself regardless of what the telemetry says afterwards.
 *   - This is deliberate. The `networkQuality` snapshot reports degraded for the full
 *     60s rolling window once a single bad response lands, so any hide logic tied to the
 *     telemetry "recovering" kept the pill glued on screen for up to a minute — the
 *     long-standing "appears and never goes away" complaint. A bad network is a transient
 *     thing to *flag*, not a status to *mirror*: a 4s blip tells the user, then gets out
 *     of the way. (History: 489ff84b, 84c88429, 8338b67d, 8b65bbc1 all tried to fix the
 *     stickiness on the telemetry/polling layer and it kept coming back — different layer.)
 *   - RESHOW_COOLDOWN_MS prevents nagging: after a blip the pill stays quiet for a while
 *     even if the network is still bad, so it never flickers or re-shows every second.
 *   - State is reset when the session resumes from pause so stale degraded data from
 *     before the pause does not immediately re-show the pill.
 *
 * Polling reliability:
 *   The 1s poll reads `getDiagnostics` through a ref rather than depending on it.
 *   `getDiagnostics` is recreated on every parent render (it's an inline arrow in the
 *   session hook's return), so listing it in the interval effect's deps tore down and
 *   recreated the setInterval on every render. During active conversation the parent
 *   re-renders faster than once per second (AI-speaking / streaming transcript state),
 *   which cleared the interval before `tick` ever fired — and `tick` is the only place
 *   `setVisible(false)` runs. Net effect: once shown, the pill could never hide. Reading
 *   through a ref lets the interval live across renders and fire on a stable 1Hz cadence.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type DiagnosticsSnapshot = {
  playback: {
    networkQuality?: {
      recentResponseCount: number;
      recentDegradedResponseCount: number;
      recentMaxPrebufferMs: number;
      recentAnyJitter: boolean;
      recentAnyAudibleGap: boolean;
    };
  } | null;
} | null;

interface NetworkQualityPillProps {
  getDiagnostics: () => DiagnosticsSnapshot;
  /** When true the pill is hidden and its internal state is reset, so it does not
   *  re-appear immediately after the session resumes based on stale pre-pause data. */
  isPaused?: boolean;
}

const POLL_INTERVAL_MS = 1000;
// How long the pill stays up once shown. Short on purpose — it is a flag, not a status.
// At a 1s poll the real on-screen time lands in the 4–5s range; never the 60s window.
const SHOW_DURATION_MS = 4000;
// After a blip, stay quiet for this long before the pill can auto-show again, so a
// genuinely bad network produces an occasional reminder rather than constant flicker.
const RESHOW_COOLDOWN_MS = 30000;

export default function NetworkQualityPill({ getDiagnostics, isPaused = false }: NetworkQualityPillProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  // Timestamp (performance.now) at which the current blip should hide. Non-null ⇒ showing.
  const hideAtRef = useRef<number | null>(null);
  // Earliest timestamp at which a new blip may start (set after each blip ends).
  const cooldownUntilRef = useRef(0);
  const wasPausedRef = useRef(false);

  // Keep the latest getDiagnostics in a ref so the polling interval below does not
  // depend on its (per-render) identity. See "Polling reliability" in the file header.
  const getDiagnosticsRef = useRef(getDiagnostics);
  getDiagnosticsRef.current = getDiagnostics;

  // When the session resumes from pause, reset internal state so stale degraded
  // diagnostics from before the pause don't immediately re-trigger the pill.
  useEffect(() => {
    if (wasPausedRef.current && !isPaused) {
      // Transitioning from paused → active: clear any lingering visible state.
      setVisible(false);
      hideAtRef.current = null;
      cooldownUntilRef.current = 0;
    }
    wasPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    // Don't poll while paused — the audio pipeline is intentionally stopped and
    // diagnostics would reflect that, not a real network problem.
    if (isPaused) return;

    const tick = () => {
      const now = performance.now();

      // Auto-dismiss: once the short display window elapses, hide regardless of what the
      // telemetry says now, and open the cooldown so we don't immediately re-show.
      if (hideAtRef.current !== null && now >= hideAtRef.current) {
        setVisible(false);
        hideAtRef.current = null;
        cooldownUntilRef.current = now + RESHOW_COOLDOWN_MS;
      }

      const snap = getDiagnosticsRef.current();
      const nq = snap?.playback?.networkQuality;
      if (!nq) return;

      // Show when any recent response had a real audible artefact (scheduling underrun
      // or sustained buffer starvation), OR when multiple recent responses both had them
      // (sustained bad network). Internal warnings (jitter, single starvation bumps,
      // slow chunk arrival) no longer qualify — the user doesn't hear those.
      const isDegraded =
        nq.recentAnyAudibleGap ||
        nq.recentDegradedResponseCount >= 2;

      // Rising-edge show only: start a blip when degraded, not already showing, and past
      // the cooldown. The telemetry decides *whether* to flag, never *how long* to show.
      if (isDegraded && hideAtRef.current === null && now >= cooldownUntilRef.current) {
        setVisible(true);
        hideAtRef.current = now + SHOW_DURATION_MS;
      }
    };

    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isPaused]);

  if (isPaused || !visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-3 top-[106px] md:left-auto md:right-32 md:top-4 z-40 pointer-events-none flex items-center h-12 max-w-[calc(100vw-6rem)] md:max-w-none"
    >
      <div className="flex items-center gap-2 bg-amber-900/85 text-amber-50 backdrop-blur-md rounded-full px-3 py-1.5 border border-amber-400/30 shadow-lg text-[11px] md:text-xs font-medium">
        <span
          className="w-2 h-2 rounded-full bg-amber-300 animate-pulse shrink-0"
          aria-hidden="true"
        />
        <span className="truncate">
          {t(
            'audio.networkDegraded',
            'Ligação lenta — áudio pode atrasar'
          )}
        </span>
      </div>
    </div>
  );
}
